/**
 * Device tags (catheter / stoma / PEG) — display helpers.
 *
 * The API ships `devices` as a sibling of critical_information on both the
 * listing `card_summary` and the resident overview:
 *   [{ key, label, detail, icon, sources, source_titles, last_check: {at, by, label} | null }]
 * A tag is a standing care fact, not an alert: it has one quiet blue style and
 * never folds into "+N". The API decides WHETHER a resident has a device (plan
 * or register); this file only formats what it was given.
 *
 * ONE exception (catheter Option B, 28 Sep 2026): while a catheter blockage is
 * still not draining the tag carries
 *   alert: { key: "catheter_not_draining", tone: "red",
 *            text: "not draining since 06:10", since, blockage_uuid }
 * and turns red — "🧪 Catheter · not draining since 06:10" — until someone
 * records "Catheter draining again" or "Catheter changed".
 *
 * Tube Feeding (28 Sep 2026) uses the same red tag: its alert reads
 *   alert: { level: "critical" | "high", tone: "red", text: "Tube out 09:40" }
 * (a `level` alone also reads red), and a tag may carry
 *   running: "Feed running since 20:02"   — a quiet detail, never a colour.
 */

/** Shown when no relevant observation falls inside the API's 90-day window. */
export const NO_RECENT_CHECK = "none in the last 90 days";

/** Well-formed device entries only, in API order. */
export const normaliseDevices = (devices) =>
  Array.isArray(devices)
    ? devices.filter(
        (device) =>
          device &&
          typeof device.key === "string" &&
          typeof device.label === "string" &&
          device.label.trim() !== "",
      )
    : [];

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** "today 14:10" / "yesterday 09:05" / "18 Sep 14:10". */
export const formatDeviceCheckTime = (value, now = new Date()) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const time = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (sameDay(date, now)) return `today ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return `yesterday ${time}`;
  const day = date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
  return `${day} ${time}`;
};

/** "Catheter emptied today 14:10 · Marisol Ibarra", or null when never checked. */
export const formatDeviceLastCheck = (lastCheck, now = new Date()) => {
  if (!lastCheck?.at) return null;
  const when = formatDeviceCheckTime(lastCheck.at, now);
  if (!when) return null;
  const what = lastCheck.label ? `${lastCheck.label} ${when}` : when;
  return [what, lastCheck.by].filter(Boolean).join(" · ");
};

/** "Continence Care Plan · Aids & Equipment register". */
export const formatDeviceSources = (device) => {
  const titles = Array.isArray(device?.source_titles)
    ? device.source_titles.filter(Boolean)
    : [];
  return titles.join(" · ");
};

/**
 * The tag's red alert, or null. Only a well-formed red alert counts — any
 * other tone keeps the quiet blue fact style.
 */
// Alert levels that read red even without `tone` (the tube-feeding tag alert).
const RED_ALERT_LEVELS = ["critical", "high"];

export const deviceAlert = (device) => {
  const alert = device?.alert;
  if (!alert || typeof alert !== "object") return null;
  if (alert.tone !== "red" && !RED_ALERT_LEVELS.includes(alert.level))
    return null;
  return alert;
};

/** "Feed running since 20:02", or null. */
export const deviceRunning = (device) =>
  typeof device?.running === "string" && device.running.trim()
    ? device.running.trim()
    : null;

/** The quiet text after the label on a tag that isn't red: detail · running. */
export const formatDeviceDetail = (device) =>
  [device?.detail, deviceRunning(device)].filter(Boolean).join(" · ") || null;

/**
 * "not draining since 06:10". The API writes the words; an alert that only
 * carries `since` gets the same sentence from the time.
 */
export const formatDeviceAlertText = (device, now = new Date()) => {
  const alert = deviceAlert(device);
  if (!alert) return null;
  const text = typeof alert.text === "string" ? alert.text.trim() : "";
  if (text) return text;
  const since = formatDeviceCheckTime(alert.since, now);
  if (!since) return "not draining";
  // "today 06:10" → "since 06:10"; "yesterday 23:40" / "24 Sep 06:10" as they are.
  return `not draining since ${since.replace(/^today /, "")}`;
};

/** Screen-reader label: "Catheter, indwelling" (+ ", not draining since 06:10"). */
export const formatDeviceAriaLabel = (device) =>
  [
    device?.label,
    device?.detail,
    deviceRunning(device),
    formatDeviceAlertText(device),
  ]
    .filter(Boolean)
    .join(", ");
